/* eslint-disable camelcase */
import React from 'react';
import Tooltip from '../common/Tooltip';


export type TSettingsData = {
    BACKUPS_ENABLED: boolean;
    BACKUPS_PER_FREELANCER_FOR_NEW_CLIENTS: number;
    BACKUPS_PER_FREELANCER_FOR_ALL_CLIENTS: number;
    BACKUPS_REQUIRED_FOR_UNRELIABLE_APPLICANTS: boolean;
    BACKUPS_REQUIRED_FOR_NEW_CLIENTS: boolean;
    BACKUPS_OPEN_BEFORE_START: string;
    BACKUPS_CLOSE_BEFORE_START: string;
    BACKUPS_CANCEL_AFTER_START: string;
    BACKUPS_DESCRIPTION: string;
    BACKUP_CONFIRMED_HEADER: string;
    BACKUP_INVITATION_HEADER: string;
    BACKUPS_FREELANCER_RATE_INCREASE_GBP: number;
    TRACKING_AUTOMATIC_ACTIONS_ENABLED: boolean;
    DEPARTURE_CONFIRMATION_TIME: string;
    CLOCK_IN_EARLY_LIMIT: string;
    CLOCK_OUT_MAX_LIMIT: string;
    EMERGENCY_FREELANCER_RATE_INCREASE_GBP: number;
    EMERGENCY_JOB_START_TIME_INCREASE: string;
    EMERGENCY_JOB_DESCRIPTION: string;
    TRACKING_AUTOMATIC_ACTIONS_START: string;
    START_BOOKING_LOCATION_TRACKING: string;
    BAD_CLIENT_FEEDBACK_THRESHOLD: number;
    RECURRENCE_DAYS_AHEAD: number;
    MIN_JOB_NOTICE: string;
    MIN_JOB_DURATION: string;
    MINIMUM_DELAY_BETWEEN_JOBS: string;
    MAXIMUM_MATCHING_SCORE_THRESHOLD: number;
    MINIMUM_MATCHING_SCORE_THRESHOLD: number;
    INVITATIONS_INTERVAL: string;
    GROUP_INVITATIONS: boolean;
    DELAY_CREATE_INVITATIONS_WHEN_SERIES: string;
    BOOKINGS_EMAIL: string;
    BAD_FEEDBACK_UNPUBLISH_COUNT: number;
    STRIKES_VOIDING_JOBS: number;
    STRIKES_UNPUBLISH_THRESHOLD: number;
    BAD_FEEDBACK_UNPUBLISH_THRESHOLD: number;
    MATCHING_COEFFICIENT_RATING: number;
    MATCHING_COEFFICIENT_CLIENT_RATING: number;
    MATCHING_COEFFICIENT_CLIENT_CATEGORY: number;
    MATCHING_COEFFICIENT_MONTHLY_ACTIVITY: number;
    MATCHING_COEFFICIENT_JOB_DISTANCE: number;
    DEFAULT_MATCHING_MODE: string;
    DYNAMIC_PRICING_ENABLED: boolean;
    DEFAULT_CLIENT_PAY_PER_HOUR: number;
    DEFAULT_COMMISSION: number;
    OFFPEAK_START_TIME: string;
    PEAK_START_TIME: string;
    CHECKOUT_EXPIRATION: string;
    CANCELLED_BOOKING_CHARGES: any[];
    INTERVIEWS_ONLINE: boolean;
    CENTRAL_ONBOARDING_ENABLED: boolean;
    ONBOARDING_EMAIL: string;
    INDUCTION_SESSION_ADDRESS: string;
    INTERVIEW_ENABLED: boolean;
    PRESENTATION_ENABLED: boolean;
    COMMUNITIES_ENABLED: boolean;
    FREELANCER_FEEDBACK_ENABLED: boolean;
    NPS_CAMPAIGNS: boolean;
    CLIENT_FEEDBACK_ENABLED: boolean;
    DASHBOARD_SMS_ALLOWED: boolean;
    SMS_USE_AGENT_EMAIL: boolean;
    EMAIL_WORKER_ON_BOOKING_CONFIRMATION: boolean;
    VOICE_CALLS_WINDOW_END: string;
    VOICE_CALLS_WINDOW_START: string;
    NOTIFICATIONS_MANAGEMENT: { [key: string]: boolean; };
    IS_ONBOARDING: boolean;
    DEFAULT_ASSIGNMENT_DETAILS: { [key: string]: boolean; };
    ALLOWED_SERVICES_ON_SIGNUP: string[];
    REQUIRED_PROFILE_FIELDS: string[];
    REQUIRED_PROFILE_FIELDS_ONBOARDING_ONLY: string[];
    VALID_POSTCODES: { [key: string]: string; };
    CLIENT_PORTAL_NOTIFICATION_NUMBERS: string[];
}

type TSmsManagement = {
    settingsData: TSettingsData;
    onSettingChange: (settingsData: any) => void;
}

export function SmsManagement({
    settingsData,
    onSettingChange,
}: TSmsManagement) {

    return (
        <div className="system-settings-section">
            <div className="system-settings-section__header">
                <div className="system-settings-section__title">
                    Sms Management
                </div>
            </div>
            <div className="system-settings-section__content">
                <table className="table-settings">
                    <tbody>
                        <tr>
                            <td>
                                <span>Dashboard sms allowed</span>
                                <span className="table-settings__tooltip">
                                    <Tooltip
                                        tooltip="Allow sending sms via dashboard."
                                    >
                                        <i className="icon icon-infomation-circle" />
                                    </Tooltip>
                                </span>
                            </td>
                            <td>
                                <label className="settings-checkbox">
                                    <input
                                        type="checkbox"
                                        checked={
                                            settingsData.DASHBOARD_SMS_ALLOWED
                                        }
                                        onChange={event => {
                                                onSettingChange({
                                                    DASHBOARD_SMS_ALLOWED:
                                                        event.target.checked,
                                                });
                                            }}
                                    />
                                    <div className="settings-checkbox__container">
                                        <div className="settings-checkbox__indicator" />
                                        <span className="settings-checkbox__yes-label">
                                            Yes
                                        </span>
                                        <span className="settings-checkbox__no-label">
                                            No
                                        </span>
                                    </div>
                                </label>
                            </td>
                        </tr>
                        <tr>
                            <td>
                                <span>Use agent email</span>
                                <span className="table-settings__tooltip">
                                    <Tooltip
                                        tooltip="Use agent's individual email address instead of the agency's main email."
                                    >
                                        <i className="icon icon-infomation-circle" />
                                    </Tooltip>
                                </span>
                            </td>
                            <td>
                                <label className="settings-checkbox">
                                    <input
                                        type="checkbox"
                                        checked={
                                            settingsData.SMS_USE_AGENT_EMAIL
                                        }
                                        onChange={event => {
                                                onSettingChange({
                                                    SMS_USE_AGENT_EMAIL:
                                                        event.target.checked,
                                                });
                                            }}
                                    />
                                    <div className="settings-checkbox__container">
                                        <div className="settings-checkbox__indicator" />
                                        <span className="settings-checkbox__yes-label">
                                            Yes
                                        </span>
                                        <span className="settings-checkbox__no-label">
                                            No
                                        </span>
                                    </div>
                                </label>
                            </td>
                        </tr>
                        <tr>
                            <td>
                                <span>Client portal notifications</span>
                                <span className="table-settings__tooltip">
                                    <Tooltip
                                        tooltip="List of numbers that will receive notifications when a self-serve
                                                 client signs up or books a job. Please enter numbers separated with
                                                 a comma."
                                    >
                                        <i className="icon icon-infomation-circle" />
                                    </Tooltip>
                                </span>
                            </td>
                            <td>
                                <textarea
                                    className="settings-long-textfield"
                                    rows={1}
                                    defaultValue={settingsData.CLIENT_PORTAL_NOTIFICATION_NUMBERS?.join(', ')}
                                    onBlur={event => {
                                        const numbers = event.target.value
                                            .split(',')
                                            .map(num => num.trim())
                                            .filter(num => num !== '');
                                        onSettingChange({ CLIENT_PORTAL_NOTIFICATION_NUMBERS: numbers });
                                    }}
                                />
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </div>
    );
}
